import React, { useCallback, useEffect, useState } from "react";
import { useTicket } from "@/context/TicketContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { Check, RefreshCcw, Search } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useGetAllCounterQuery, useGetCounterListQuery } from "@/services/CounterApi";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { debounce } from "lodash";
import { Input } from "@/components/ui/Input";
import { StatusFilter } from "@/constants/counter-management";
import { useGetStaffListQuery } from "@/services/StaffsApi";
import TableFilters from "@/components/ui/table/DataTableFilter";
import { toast } from "@/components/ui/UseToast";
import { useGetTagsQuery } from "@/services/TagsApi";
import { ErrorType } from "@/app/types";
import { useScopedRoute } from "@/utils/useScopedRoute";
import { TagListItem } from "@/app/types/tags";

interface RouteTicketsProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}
const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};
const RouteTicket: React.FC<RouteTicketsProps> = ({ isOpen, setOpen }) => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const { data: staffData } = useGetStaffListQuery({ query: "" });
  const [filterData, setFilterData] = React.useState({});
  const [openFilter, setOpenFilter] = React.useState(false);
  // Add these state variables near your other state declarations
  const [searchCriteria, setSearchCriteria] = useState({});
  const [inputValue, setInputValue] = useState({});
  const [selectedService, setSelectedService] = useState<TagListItem | null>(null);
  const [selectCounterId, setSelectedCounterId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const { currentTicket, loading, routeTicket, isAccrossDepartment,setAccrossDepartment } =
    useTicket();
  const scopedRoute = useScopedRoute();
  const eventId = scopedRoute?.eventId;
  // Setup debounce for search
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setDebouncedSearchTerm(term);
    }, 500),
    []
  );
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    // Prevent leading spaces and multiple consecutive spaces
    let formattedValue = rawValue;

    // Remove leading spaces
    if (rawValue.startsWith(" ")) {
      formattedValue = rawValue.trimStart();
    }

    // Replace multiple spaces with a single space
    formattedValue = formattedValue.replace(/\s{2,}/g, " ");

    setSearchTerm(formattedValue);
    debouncedSearch(formattedValue);
  };
  const buildQueryParams = (filters: Record<string, any>) => {
    if (!filters || Object.keys(filters).length === 0) return "";

    let params = "";

    // Handle staff_ids as an array if needed
    if (filters.staff_ids && Array.isArray(filters.staff_ids)) {
      filters.staff_ids.forEach((id: string) => {
        params += `&staff_ids=${id}`;
      });
    } else if (filters.staff_ids) {
      params += `&staff_ids=${filters.staff_ids}`;
    }

    // Handle other filter parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (key !== "staff_ids" && value) {
        // Skip staff_ids as we handled it above
        params += `&${key}=${encodeURIComponent(String(value))}`;
      }
    });

    return params;
  };
  
  const {
    data: serviceTagsData,
    refetch: servicesRefetch,
    isLoading,
  } = useGetTagsQuery(
    {
      org_id: Number(userInfo?.org_id),
      tag_type: "service",
      event_ids: eventId ? [Number(eventId)] : null,
    },
    {
      skip: !isOpen || !userInfo?.org_id,
      refetchOnMountOrArgChange: true,
    }
  );

  const serviceOptions = React.useMemo(() => {
    if (!serviceTagsData?.data) return { data: [] };
    
    let filtered = serviceTagsData.data;
    
    // Filter by department if not across department
    if (!isAccrossDepartment && userInfo?.department_id) {
       filtered = filtered.filter(tag => tag.path.includes(Number(userInfo.department_id)));
    }
    
    // Filter by search term
    if (debouncedSearchTerm) {
      filtered = filtered.filter(tag => tag.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));
    }
    
    return { data: filtered };
  }, [serviceTagsData, isAccrossDepartment, userInfo?.department_id, debouncedSearchTerm]);

  // Update the counter query to fetch all counters with proper query params
  const {
    data: counterOptions,
    refetch: counterRefetch,
  } = useGetAllCounterQuery(
    {
      query: ``
    },
  );

  const { data: departmentTagsData } = useGetTagsQuery(
    {
      org_id: Number(userInfo?.org_id),
      tag_type: "department",
    },
    {
      skip: !isOpen || !userInfo?.org_id,
    }
  );

  const departmentMap = React.useMemo(() => {
    if (!departmentTagsData?.data) return {};
    return departmentTagsData?.data.reduce((acc, dept) => {
      acc[String(dept.tag_id)] = dept.name;
      return acc;
    }, {} as Record<string, string>);
  }, [departmentTagsData]);

  const handleSaveLink = async () => {
  if (!selectedService) {
    toast({
      variant: "destructive",
      title: "Error",
      description: "Please select a service first"
    });
    return;
  }

  try {
    const result = await routeTicket(
      selectCounterId ? Number(selectCounterId) : 0, 
      selectedService.tag_id, 
      "/" + selectedService.path.join("/")
    );

    if (result.success) {
      toast({
        variant: "success",
        title: "Success",
        description: result.message
      });
      setSelectedCounterId(null);
      setSelectedService(null);
      setOpen(false);
    } else {
      // Handle the error case explicitly
      toast({
        variant: "destructive",
        title: "Error",
        description: result.message || "Failed to route ticket"
      });
    }
  } catch (error) {
    const typedError = error as ErrorType;
    const errorMessage = typedError.data?.detail || typedError.data?.message || "An unexpected error occurred";
    
    toast({
      variant: "destructive",
      title: "Error",
      description: errorMessage
    });
    console.error("Route ticket error:", error);
  }
};


  const handleSelectService = (service: TagListItem) => {
    setSelectedService(service);
    setSelectedCounterId(null); // Reset counter selection when service changes
  };

  const handleSelectCounter = (counterId: string) => {
    setSelectedCounterId(counterId);
  };


  const handleFilterChange = (filterName: string, value: string) => {
    setFilterData((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [filterName]: value,
      };
      return newFilters;
    });
  };

  const filterDisplay = [
    {
      type: "multiselect",
      name: "staff_ids",
      label: "Staff",
      options: staffData?.map((item) => ({
        value: item.value,
        label: item.label,
      })),
      function: (value: string) => handleFilterChange("staff_ids", value),
    },
  ];

  useEffect(() => {
    const updateSettings = async () => {
      if (isOpen) {
        await setAccrossDepartment();
        counterRefetch();
        servicesRefetch();
      }
    };
    updateSettings();
  }, [isOpen, debouncedSearchTerm, filterData]);

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setOpen(false);
          setSelectedCounterId(null);
        } else {
          setOpen(open);
        }
      }}
    >
      <SheetContent className="!max-w-[550px] p-6 pb-8">
        <SheetHeader className="mb-4">
          <SheetTitle>Route Ticket</SheetTitle>
          <p className="text-sm text-gray-500">
            Route ticket to the other counter
          </p>
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button onClick={servicesRefetch} className="gap-2 ">
              <RefreshCcw className="text-gray-500 h-5 w-5 " />
              Refresh List
            </Button>
            <Button
              className="text-black bg-primary hover:bg-primary/80"
              onClick={handleSaveLink}
                disabled={!selectedService|| loading} // Changed from !selectCounterId
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </div>
        </SheetHeader>

        {/* Search input */}
        <div className="mb-4 flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search counters..."
              className="pl-9"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div>
            <Button
              variant="outline"
              onClick={() => setOpenFilter(true)}
              className="flex items-center gap-2 w-fit"
            >
              Filter
            </Button>
          </div>
        </div>
        <div className="space-y-3 mt-4 overflow-y-auto fancy-scroll max-h-[calc(100vh-250px)] p-6">
           {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="p-3 animate-pulse">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </Card>
            ))}
          </div>
        ) : serviceOptions?.data && serviceOptions.data.length > 0 ? (
          <div className="space-y-4">
            {serviceOptions.data.map((service) => {
              // Fix: Filter counters properly using tag_id instead of assigned_counters
              const serviceCounters = counterOptions?.data?.filter(counter => 
                counter.tag_id.includes(service.tag_id) && 
                counter.id !== userInfo?.counter_id
              ) || [];

              console.log(`Service ${service.name} counters:`, serviceCounters); // For debugging

              return (
                <Card
                  key={service.tag_id}
                  className={`p-3 cursor-pointer hover:border-primary transition-colors
                    ${selectedService?.tag_id === service.tag_id ? "border-2 border-primary" : ""}`}
                  onClick={() => handleSelectService(service)}
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium mb-1">{service.name}</h4>
                        <p className="text-sm text-gray-500">Service #{service.code}</p>
                        <p className="text-sm text-gray-500">
                          Department: {(() => {
                            const deptId = service.path.length > 1 ? service.path[service.path.length - 2] : null;
                            return deptId ? departmentMap[String(deptId)] : 'Unknown Department';
                          })()}
                        </p>
                      </div>
                    </div>
                    
                    {selectedService?.tag_id === service.tag_id && (
                      <div className="mt-3 border-t pt-3">
                        <p className="text-sm font-medium mb-2">Select Counter (Optional):</p>
                        <div className="grid grid-cols-2 gap-2">
                          {serviceCounters.map((counter) => (
                            <button
                              key={counter.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectCounter(counter.id.toString());
                              }}
                              className={`p-2 text-sm border rounded-md hover:bg-gray-50
                                ${selectCounterId === counter.id.toString() ? 'border-primary bg-primary/10' : 'border-gray-200'}`}
                            >
                              <div className="text-left">
                                <div>Counter #{counter.code}</div>
                                <div className="text-xs text-gray-500">
                                  {counter.occupied_by && (
                                    <span>
                                      Occupied by: {
                                        counter.staff_members.find(staff => staff.id === counter.occupied_by)?.full_name || counter.occupied_by
                                    }
                                  </span>
                                )}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                        {serviceCounters.length === 0 && (
                          <p className="text-sm text-gray-500 italic">
                            No available counters for this service
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500">No services available.</p>
          </div>
        )}
        </div>
      </SheetContent>
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
    </Sheet>
  );
};

export default RouteTicket;
